// src/contracts/ops/OpsStateBus.ts
//
// OpsStateBus (v1)
// ----------------
// Single, crash-safe listener + reducer-backed store for Broadcast Ops Dashboard.
//
// Goals:
// - One global subscription point for ops events
// - Guards + reducer (no payload leakage)
// - Minimal API: getState() + subscribe()
// - Optional snapshot request/response for embeds / remote consoles
//
// IMPORTANT:
// - Do not mount multiple instances; this is intended to be a singleton.
// - This module contains runtime logic but remains witness-safe (no raw payload retention).
// - Strict invariant: NEVER leak a live state reference across boundaries.
//   All reads/subscriptions/snapshots/debug surfaces return CLONED snapshots.
//

import type { OpsDashboardStateV1 } from "@/contracts/ops/OpsDashboardModel";
import { createEmptyOpsDashboardState } from "@/contracts/ops/OpsDashboardModel";

import {
  SYNK_WIRING_READY_EVENT,
  SYNK_OPS_METRIC_EVENT,
  SYNK_OPS_VIOLATION_EVENT,
  SYNK_OPS_CAPABILITY_EVENT,
  SYNK_OPS_ACTION_EVENT,
  SYNK_OPS_TIMELINE_EVENT,
  SYNK_OPS_SNAPSHOT_REQUEST_EVENT,
  SYNK_OPS_SNAPSHOT_EVENT,
  type SynkWiringReadyDetailV1,
  type SynkOpsMetricDetailV1,
  type SynkOpsViolationDetailV1,
  type SynkOpsCapabilityDetailV1,
  type SynkOpsActionDetailV1,
  type SynkOpsTimelineDetailV1,
  type SynkOpsSnapshotRequestDetailV1,
} from "@/contracts/ops/OpsEvents";

import {
  isWiringReadyV1,
  isOpsMetricV1,
  isOpsViolationV1,
  isOpsCapabilityV1,
  isOpsActionV1,
  isOpsTimelineV1,
} from "@/contracts/ops/OpsEventGuards";

import {
  reduceOpsState,
  type OpsReducerInput,
} from "@/contracts/ops/OpsStateReducer";

import { cloneState } from "@/contracts/ops/opsClone";

/* ---------------------------------------
 * Singleton store
 * -------------------------------------- */

type Subscriber = (s: OpsDashboardStateV1) => void;

let _state: OpsDashboardStateV1 = createEmptyOpsDashboardState();

// Use Set for subscribers (dedupe + stable unsubscribe)
let _subs = new Set<Subscriber>();

let _installed = false;
let _uninstall: (() => void) | null = null;

/* ---------------------------------------
 * Public API
 * -------------------------------------- */

/**
 * getState()
 * ----------
 * Strict snapshot semantics: returns a cloned snapshot (never the live reference).
 */
export function getState(): OpsDashboardStateV1 {
  return cloneState(_state);
}

/**
 * subscribe()
 * -----------
 * Adds a subscriber and immediately emits the current state snapshot.
 * Strict snapshot semantics: subscriber always receives a cloned snapshot.
 */
export function subscribe(fn: Subscriber): () => void {
  _subs.add(fn);

  // Emit immediately (BehaviorSubject-like) with a safe clone.
  try {
    fn(cloneState(_state));
  } catch {
    // ignore subscriber exceptions
  }

  return () => {
    _subs.delete(fn);
  };
}

/**
 * Install the OpsStateBus (idempotent).
 * Call once in AppShell or in an Enterprise/Ops route.
 */
export function installOpsStateBus(): () => void {
  if (_installed) return _uninstall ?? (() => {});
  _installed = true;

  const notify = () => {
    // Clone per subscriber to enforce strict "no live reference" semantics,
    // even if a subscriber mutates its input.
    for (const s of Array.from(_subs)) {
      try {
        s(cloneState(_state));
      } catch {
        // never let subscriber crash the bus
      }
    }
  };

  const onAny = (input: OpsReducerInput) => {
    try {
      _state = reduceOpsState(_state, input);
      notify();
    } catch {
      // never throw from the bus
    }
  };

  const onWiringReady = (e: Event) => {
    const d = (e as CustomEvent).detail as unknown;
    if (!isWiringReadyV1(d)) return;
    onAny({ type: "wiring_ready", d: d as SynkWiringReadyDetailV1 });
  };

  const onMetric = (e: Event) => {
    const d = (e as CustomEvent).detail as unknown;
    if (!isOpsMetricV1(d)) return;
    onAny({ type: "metric", d: d as SynkOpsMetricDetailV1 });
  };

  const onViolation = (e: Event) => {
    const d = (e as CustomEvent).detail as unknown;
    if (!isOpsViolationV1(d)) return;
    onAny({ type: "violation", d: d as SynkOpsViolationDetailV1 });
  };

  const onCapability = (e: Event) => {
    const d = (e as CustomEvent).detail as unknown;
    if (!isOpsCapabilityV1(d)) return;
    onAny({ type: "capability", d: d as SynkOpsCapabilityDetailV1 });
  };

  const onAction = (e: Event) => {
    const d = (e as CustomEvent).detail as unknown;
    if (!isOpsActionV1(d)) return;
    onAny({ type: "action", d: d as SynkOpsActionDetailV1 });
  };

  const onTimeline = (e: Event) => {
    const d = (e as CustomEvent).detail as unknown;
    if (!isOpsTimelineV1(d)) return;
    onAny({ type: "timeline", d: d as SynkOpsTimelineDetailV1 });
  };

  // Optional: snapshot request handler (useful for embedded consoles)
  const onSnapshotRequest = (e: Event) => {
    const d = (e as CustomEvent).detail as unknown;
    const req = d as Partial<SynkOpsSnapshotRequestDetailV1> | undefined;

    // Cheap validation: must at least be object-like with v=1 if present
    if (req && typeof req === "object") {
      const v = (req as any).v;
      if (v !== undefined && v !== 1) return;
    }

    try {
      window.dispatchEvent(
        new CustomEvent(SYNK_OPS_SNAPSHOT_EVENT, {
          // Strict: send cloned snapshot, never live reference
          detail: { v: 1, at: Date.now(), state: cloneState(_state) },
        }),
      );
    } catch {
      // ignore
    }
  };

  // Attach listeners
  window.addEventListener(SYNK_WIRING_READY_EVENT, onWiringReady as EventListener);
  window.addEventListener(SYNK_OPS_METRIC_EVENT, onMetric as EventListener);
  window.addEventListener(SYNK_OPS_VIOLATION_EVENT, onViolation as EventListener);
  window.addEventListener(SYNK_OPS_CAPABILITY_EVENT, onCapability as EventListener);
  window.addEventListener(SYNK_OPS_ACTION_EVENT, onAction as EventListener);
  window.addEventListener(SYNK_OPS_TIMELINE_EVENT, onTimeline as EventListener);
  window.addEventListener(
    SYNK_OPS_SNAPSHOT_REQUEST_EVENT,
    onSnapshotRequest as EventListener,
  );

  _uninstall = () => {
    try {
      window.removeEventListener(
        SYNK_WIRING_READY_EVENT,
        onWiringReady as EventListener,
      );
      window.removeEventListener(SYNK_OPS_METRIC_EVENT, onMetric as EventListener);
      window.removeEventListener(
        SYNK_OPS_VIOLATION_EVENT,
        onViolation as EventListener,
      );
      window.removeEventListener(
        SYNK_OPS_CAPABILITY_EVENT,
        onCapability as EventListener,
      );
      window.removeEventListener(SYNK_OPS_ACTION_EVENT, onAction as EventListener);
      window.removeEventListener(
        SYNK_OPS_TIMELINE_EVENT,
        onTimeline as EventListener,
      );
      window.removeEventListener(
        SYNK_OPS_SNAPSHOT_REQUEST_EVENT,
        onSnapshotRequest as EventListener,
      );

      _installed = false;
      // NOTE: we intentionally keep state (so dashboards don't zero out mid-session)
    } catch {
      // ignore
    }
  };

  return _uninstall;
}

/* ---------------------------------------
 * Optional debug hook (dev-only usage)
 * -------------------------------------- */

/**
 * Expose a small debug surface for console inspection without leaking payloads.
 * This is safe because it only exposes the reduced ops state.
 *
 * Strict invariant: debug surfaces return cloned snapshots.
 */
export function installOpsDebugHook(): () => void {
  const w = window as any;
  w.__synk_ops = w.__synk_ops || {};

  // Tightening: returns a cloned snapshot, not the live reference
  w.__synk_ops.getState = () => cloneState(_state);

  w.__synk_ops.snapshot = () => {
    try {
      window.dispatchEvent(
        new CustomEvent(SYNK_OPS_SNAPSHOT_REQUEST_EVENT, {
          detail: { v: 1, at: Date.now(), requester: "debug" },
        }),
      );
    } catch {}
  };

  return () => {
    try {
      if (w.__synk_ops) {
        delete w.__synk_ops.getState;
        delete w.__synk_ops.snapshot;
      }
    } catch {}
  };
}
